import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { LayoutChangeEvent } from "react-native";

import { Box, type BoxRef, type BoxStyles } from "../../../../../mads";

interface MeasuredSkeletonContainerProps {
  readonly children: (width: number) => ReactNode;
  readonly sx: BoxStyles;
  readonly testID: string;
}

export function MeasuredSkeletonContainer({
  children,
  sx,
  testID,
}: MeasuredSkeletonContainerProps): React.JSX.Element {
  const [width, setWidth] = useState(0);
  const [element, setElement] = useState<Element | null>(null);

  const setContainerRef = useCallback((node: BoxRef | null) => {
    setElement(typeof Element !== "undefined" && node instanceof Element ? node : null);
  }, []);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setWidth(event.nativeEvent.layout.width);
  }, []);

  useEffect(() => {
    if (!element || typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const updateWidth = () => setWidth(element.getBoundingClientRect().width);
    updateWidth();

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return (
    <Box ref={setContainerRef} sx={sx} onLayout={handleLayout} testID={testID}>
      {width > 0 && children(width)}
    </Box>
  );
}
